import * as React from 'react';

import {
  enumInvoiceStatus,
  InvoiceStatus,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter, ThemeColor } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { $columnSelectedStatuses } from '~/modules/finance/_state/financeInvoices.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface StatusOption {
  id: InvoiceStatus;
  label: string;
}

const invoiceStatusColorMap: Record<InvoiceStatus, ThemeColor> = {
  [enumInvoiceStatus.ProForma]: 'neutral',
  [enumInvoiceStatus.Pending]: 'warning',
  [enumInvoiceStatus.Paid]: 'success',
  [enumInvoiceStatus.Voided]: 'failure',
};

interface InvoiceStatusColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INVOICE STATUS COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const InvoiceStatusColumnFilter: React.FC<
  InvoiceStatusColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedStatuses = React.useMemo(
    () => Array.from(selectedStatusesSet),
    [selectedStatusesSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(
    () => [
      {
        id: enumInvoiceStatus.ProForma,
        label: t.invoices.list.status.badge.proForma,
      },
      {
        id: enumInvoiceStatus.Pending,
        label: t.invoices.list.status.badge.pending,
      },
      {
        id: enumInvoiceStatus.Paid,
        label: t.invoices.list.status.badge.paid,
      },
      {
        id: enumInvoiceStatus.Voided,
        label: t.invoices.list.status.badge.voided,
      },
    ],
    [
      t.invoices.list.status.badge.proForma,
      t.invoices.list.status.badge.pending,
      t.invoices.list.status.badge.paid,
      t.invoices.list.status.badge.voided,
    ],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as InvoiceStatus[]));
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <FilterLabel
        label={item.label}
        color={invoiceStatusColorMap[item.id]}
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@InvoiceStatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.invoices.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
